/* eslint-disable no-restricted-syntax */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import Layout from '../components/Layout';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, TrendingDown, Minus, Eye, ChevronLeft } from 'lucide-react';
import ReportModal from '../components/ReportModal';
import ProblemPreview from '../components/ProblemPreview';
import { Button } from '../components/common';
import { HEX_COLORS, DIFFICULTY_COLORS, LEVEL_GREEN_COLORS, DOMAIN_GREEN_COLORS } from '../styles/tokens';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  ComposedChart,
  Bar
} from 'recharts';

// KST 시간으로 변환하는 함수 추가
const formatKSTDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000)); // UTC to KST (+9 hours)

  const year = kstDate.getFullYear();
  const month = String(kstDate.getMonth() + 1).padStart(2, '0');
  const day = String(kstDate.getDate()).padStart(2, '0');
  const hours = String(kstDate.getHours()).padStart(2, '0');
  const minutes = String(kstDate.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// 간단한 날짜 형식으로 변환
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const year = date.getFullYear().toString().slice(2);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

// Rating 변화 표시를 위한 컴포넌트
const RatingChange = ({ delta }) => {
  if (delta > 0) {
    return <span className="text-sm text-red-500">▲{delta}</span>;
  } else if (delta < 0) {
    return <span className="text-sm text-blue-500">▼{Math.abs(delta)}</span>;
  }
  return <span className="text-sm text-gray-500">-</span>;
};

// 모달 컴포넌트
const ExamDetailModal = ({ exam, round, onClose }) => {
  if (!exam) return null;

  // 모달 바깥 영역 클릭 시 닫기
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={handleBackdropClick}
    >
      <div className="max-h-[85vh] w-[64%] overflow-hidden rounded-3xl bg-white shadow-xl">
        <div className="p-6">
          <div className="mb-6 flex items-center justify-between">
            <div className="space-y-1">
              <h3 className="text-xl font-bold">시험 상세 정보</h3>
              <div className="text-sm text-gray-600">
                <span className="font-medium">TID: {exam.exam_id}</span>
                <span className="mx-2">·</span>
                <span>{round}회차</span>
              </div>
            </div>
            <div className="space-y-1 text-right">
              <div className="text-lg font-bold">Rating: {exam.rating}</div>
              <div className="text-sm">
                <span className="text-red-600">정답: {exam.correct_count}</span>
                <span className="mx-2">·</span>
                <span className="text-blue-600">오답: {exam.wrong_count}</span>
                <span className="mx-2">·</span>
                <span className="text-gray-600">
                  정답률: {((exam.correct_count / (exam.correct_count + exam.wrong_count)) * 100).toFixed(1)}%
                </span>
              </div>
              <div>
                변화량: <RatingChange delta={exam.delta} />
              </div>
            </div>
          </div>

          <div className="max-h-[calc(80vh-12rem)] overflow-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">문제번호</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">문제 ID</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">결과</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">난이도</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">문제 Rating</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">기대 정답률</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">Rating</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500">변화량</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {exam.sequence.map((problem, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="px-4 py-2 text-center text-sm">{index + 1}</td>
                    <td className="px-4 py-2 text-center text-sm">{problem[0]}</td>
                    <td className="px-4 py-2 text-center text-sm">
                      {problem[6] ? (
                        <span className="font-bold text-blue-500">O</span>
                      ) : (
                        <span className="font-bold text-red-500">X</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-sm">{problem[1]}</td>
                    <td className="px-4 py-2 text-center text-sm">{problem[2]}</td>
                    <td className="px-4 py-2 text-center text-sm">{problem[3]}%</td>
                    <td className="px-4 py-2 text-center text-sm">{problem[4]}</td>
                    <td className="px-4 py-2 text-center text-sm">
                      <RatingChange delta={problem[5]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border-t bg-gray-50 px-6 py-4">
          <Button
            variant="secondary"
            onClick={onClose}
            className="w-full"
          >
            닫기
          </Button>
        </div>
      </div>
    </div>
  );
};

// 마스킹 함수들
const maskName = (name) => {
  if (!name) return '';
  return name.charAt(0) + '*'.repeat(name.length - 1);
};

const maskEmail = (email) => {
  if (!email) return '';
  const [localPart, domain] = email.split('@');
  return localPart.charAt(0) + '*'.repeat(localPart.length - 1) + '@' + domain;
};

const maskSchoolName = (school) => {
  if (!school) return '';
  return school.charAt(0) + '*'.repeat(school.length - 1);
};

function Datapage() {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const { stamp_id } = useParams();
  const [rankData, setRankData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStamp, setSelectedStamp] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [selectedRound, setSelectedRound] = useState(null);
  const [isMasked, setIsMasked] = useState(() => {
    return JSON.parse(localStorage.getItem('datapageMaskingEnabled')) || false;
  });
  const [selectedTree, setSelectedTree] = useState('전체');
  const [activeExamId, setActiveExamId] = useState(null);
  
  // 리포트 모달 상태
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportExamData, setReportExamData] = useState(null);
  
  // 문제 보기 모달 상태
  const [isProblemPreviewOpen, setIsProblemPreviewOpen] = useState(false);
  const [previewProblemId, setPreviewProblemId] = useState(null);
  const [previewCorrect, setPreviewCorrect] = useState(null);
  const [previewNumber, setPreviewNumber] = useState(null);

  // 모바일 뷰 관련 상태
  const [isMobileView, setIsMobileView] = useState(false);
  const [currentMobilePanel, setCurrentMobilePanel] = useState('students'); // 'students', 'exams', 'details'
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);



  // 반응형 레이아웃을 위한 useEffect 추가
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsMobileView(width < 720);
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // 초기 로드 시 실행

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 학생 선택 시 모바일 뷰에서 자동으로 시험 패널로 이동
  useEffect(() => {
    if (isMobileView && selectedStamp) {
      setCurrentMobilePanel('exams');
    }
  }, [selectedStamp, isMobileView]);

  // 시험 선택 시 모바일 뷰에서 자동으로 상세 패널로 이동
  useEffect(() => {
    if (isMobileView && activeExamId) {
      setCurrentMobilePanel('details');
    }
  }, [activeExamId, isMobileView]);

  useEffect(() => {
    const fetchRankData = async () => {
      if (rankData) return;

      try {
        const token = await getAccessToken();
        if (!token) {
          navigate('/signin');
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/elotest/rank`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch rank data');
        }

        const data = await response.json();
        setRankData(data.data);
      } catch (error) {
        console.error('Error fetching rank data:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRankData();
  }, [getAccessToken, navigate, rankData]);

  useEffect(() => {
    if (rankData && stamp_id) {
      const stamp = rankData.find(s => String(s.stamp_id) === String(stamp_id));
      if (stamp) {
        setSelectedStamp(stamp);
      }
    }
  }, [rankData, stamp_id]);

  const handleSearchChange = (e) => {
    const filteredValue = e.target.value.replace(/[^a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ\s]/g, '');
    setSearchQuery(filteredValue);
  };

  const treeNames = useMemo(() => {
    if (!rankData) return ['전체'];
    const names = [...new Set(rankData.map(stamp => stamp.tree_name))];
    return ['전체', ...names.sort()];
  }, [rankData]);

  const filteredRankData = useMemo(() => {
    if (!rankData) return null;

    let filtered = rankData;
    
    if (selectedTree !== '전체') {
      filtered = filtered
        .filter(stamp => stamp.tree_name === selectedTree)
        .sort((a, b) => b.rating - a.rating)
        .map((stamp, index) => ({
          ...stamp,
          rank: index + 1
        }));
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(stamp =>
        stamp.student_name.toLowerCase().includes(query) ||
        stamp.school_name.toLowerCase().includes(query) ||
        stamp.student_email.toLowerCase().includes(query) ||
        stamp.edu_ver.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [rankData, searchQuery, selectedTree]);

  const handleCardClick = (stamp) => {
    setSelectedStamp(stamp);
    setActiveExamId(null); // 시험 선택 초기화
    
    if (!isMobileView) {
      navigate(`/data/${stamp.stamp_id}`);
    } else {
      setCurrentMobilePanel('exams');
    }
  };

  const handleExamClick = (exam, round) => {
    setSelectedExam(exam);
    setSelectedRound(round);
  };

  const handleSelectExam = (exam) => {
    setActiveExamId(exam.exam_id);
    
    if (isMobileView) {
      setCurrentMobilePanel('details');
    }
  };

  useEffect(() => {
    if (selectedStamp) {
      const element = document.getElementById(`stamp-${selectedStamp.stamp_id}`);
      if (element) {
        const container = element.parentElement.parentElement;
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [selectedStamp]);

  const handleMaskingChange = (e) => {
    const newValue = e.target.checked;
    setIsMasked(newValue);
    localStorage.setItem('datapageMaskingEnabled', JSON.stringify(newValue));
  };

  // 난이도별 정답률 계산 함수
  const getLevelAchievementData = (stamp, examId) => {
    if (!stamp || !stamp.stamp_record) {
      return [1, 2, 3, 4, 5].map(level => ({
        level,
        currentAcc: 0,
        currentCorrect: 0,
        currentTotal: 0
      }));
    }

    // 시험별 정보가 없으므로 전체 통계만 사용
    const correctByLevel = stamp.stamp_record[0] || [0, 0, 0, 0, 0];
    const incorrectByLevel = stamp.stamp_record[1] || [0, 0, 0, 0, 0];

    return [1, 2, 3, 4, 5].map((level, index) => {
      const correct = correctByLevel[index] || 0;
      const incorrect = incorrectByLevel[index] || 0;
      const total = correct + incorrect;
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

      return {
        level,
        currentAcc: accuracy,
        currentCorrect: correct,
        currentTotal: total
      };
    });
  };

  // 시험별 그래프 데이터
  const getExamChartData = (stamp) => {
    if (!stamp?.exam_list) return [];

    // 배열을 뒤집어서 과거 시험이 왼쪽에, 최근 시험이 오른쪽에 표시되도록 함
    return stamp.exam_list.slice().reverse().map((exam, index) => {
      const totalQuestions = exam.correct_count + exam.wrong_count;
      const accuracy = totalQuestions > 0 ? Math.round((exam.correct_count / totalQuestions) * 100) : 0;
      
      // 맞춘 문제들의 난이도별 분포 계산
      const correctProblems = exam.sequence ? exam.sequence.filter(problem => problem[6] === true) : [];
      const difficultyDistribution = [0, 0, 0, 0, 0]; // Level 1~5
      
      correctProblems.forEach(problem => {
        const difficulty = problem[1]; // 난이도 (1~5)
        if (difficulty >= 1 && difficulty <= 5) {
          difficultyDistribution[difficulty - 1]++;
        }
      });
      
      // 각 난이도별 비율 계산 (정답률 기준으로 환산)
      const totalCorrect = correctProblems.length;
      const difficultyRatios = difficultyDistribution.map(count => 
        totalCorrect > 0 ? (count / totalCorrect) * accuracy : 0
      );
      
      return {
        x: index + 1, // x축: 회차 (왼쪽부터 시작)
        rating: exam.rating,
        accuracy: accuracy, // 정답률 추가
        exam_id: exam.exam_id,
        delta: exam.delta,
        difficulty: exam.difficulty / 100, // 전체 시험 난이도
        // 스택형 막대그래프를 위한 난이도별 데이터
        level1: difficultyRatios[0], // Level 1 맞춘 비율
        level2: difficultyRatios[1], // Level 2 맞춘 비율
        level3: difficultyRatios[2], // Level 3 맞춘 비율
        level4: difficultyRatios[3], // Level 4 맞춘 비율
        level5: difficultyRatios[4], // Level 5 맞춘 비율
        correctByLevel: difficultyDistribution, // 난이도별 맞춘 문제 수
        totalCorrect: totalCorrect // 총 맞춘 문제 수
      };
    });
  };

  // 난이도별 레이더 차트 데이터
  const getLevelRadarData = (stamp) => {
    if (!stamp || !stamp.stamp_record) {
      return [1, 2, 3, 4, 5].map(level => ({
        subject: `LV ${level}`,
        A: 0,
        fullMark: 100,
      }));
    }

    const correctByLevel = stamp.stamp_record[0] || [0, 0, 0, 0, 0];
    const incorrectByLevel = stamp.stamp_record[1] || [0, 0, 0, 0, 0];

    return [1, 2, 3, 4, 5].map((level, index) => {
      const correct = correctByLevel[index] || 0;
      const incorrect = incorrectByLevel[index] || 0;
      const total = correct + incorrect;
      const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

      return {
        subject: `LV ${level}`,
        A: accuracy,
        fullMark: 100,
      };
    });
  };

  // 정답률 계산 함수
  const getAccuracyData = (stamp) => {
    if (!stamp || !stamp.stamp_record) {
      return {
        accuracy: 0,
        correctCount: 0,
        totalCount: 0
      };
    }

    const correctByLevel = stamp.stamp_record[0] || [];
    const incorrectByLevel = stamp.stamp_record[1] || [];
    
    const totalCorrect = correctByLevel.reduce((sum, count) => sum + count, 0);
    const totalIncorrect = incorrectByLevel.reduce((sum, count) => sum + count, 0);
    const totalQuestions = totalCorrect + totalIncorrect;
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;

    return {
      accuracy,
      correctCount: totalCorrect,
      totalCount: totalQuestions
    };
  };

  // 선택된 시험 정보 가져오기
  const getSelectedExamInfo = (stamp, examId) => {
    if (!stamp || !stamp.exam_list || !examId) return null;
    return stamp.exam_list.find(exam => exam.exam_id === examId) || null;
  };

  // 현재 선택된 시험
  const activeExam = useMemo(() => {
    if (!selectedStamp || !activeExamId) return null;
    return getSelectedExamInfo(selectedStamp, activeExamId);
  }, [selectedStamp, activeExamId]);

  // 리포트 모달 열기 함수
  const handleOpenReportModal = (exam) => {
    setReportExamData({
      exam_id: exam.exam_id,
      stamp_id: selectedStamp.stamp_id
    });
    setIsReportModalOpen(true);
  };

  // 문제 보기 모달 열기 함수
  const handleOpenProblemPreview = (problem, index) => {
    const problemId = problem[0]; // 문제 ID
    const isCorrect = problem[6]; // 정답 여부
    
    setPreviewProblemId([problemId]);
    setPreviewCorrect([isCorrect]);
    setPreviewNumber([index + 1]);
    setIsProblemPreviewOpen(true);
  };

  // 모바일 뷰에서 뒤로가기 처리 함수
  const handleMobileBack = () => {
    if (currentMobilePanel === 'details') {
      setCurrentMobilePanel('exams');
    } else if (currentMobilePanel === 'exams') {
      setCurrentMobilePanel('students');
    }
  };

  return (
    <Layout>
      <div className="page-container">
          {/* 모바일 뷰 */}
          {isMobileView ? (
            <div className="flex h-[calc(100vh-7rem)] flex-col gap-4">
              {/* 모바일 헤더 */}
              {currentMobilePanel !== 'students' && (
                <div className="mb-2 flex items-center rounded-3xl bg-white p-3 shadow-lg">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleMobileBack}
                    className="mr-2 rounded-full p-1"
                  >
                    <ChevronLeft size={20} />
                  </Button>
                  <h2 className="text-base font-semibold">
                    {currentMobilePanel === 'exams' 
                      ? `${selectedStamp?.student_name || '학생'} 시험 목록` 
                      : `${selectedStamp?.student_name || '학생'} - ${formatDate(getSelectedExamInfo(selectedStamp, activeExamId)?.updated_at) || '시험'}`}
                  </h2>
                </div>
              )}

              {/* 패널 표시 (학생 목록, 시험 목록, 상세 정보 중 하나만 표시) */}
              {currentMobilePanel === 'students' && (
                <div className="flex h-full flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
                  <div className="sticky top-0 z-10 space-y-3 border-b bg-white p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-[14px] font-semibold">학생 목록</h3>
                      <div className="flex items-center gap-3">
                        <label className="flex items-center gap-1 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={isMasked}
                            onChange={handleMaskingChange}
                            className="rounded text-primary focus:ring-primary"
                          />
                          마스킹
                        </label>
                        <span className="text-xs text-gray-500">
                          {filteredRankData?.length || 0} / {rankData?.length || 0}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={selectedTree}
                        onChange={(e) => setSelectedTree(e.target.value)}
                        className="w-36 rounded-3xl border border-gray-300 px-3
                                 py-1.5 text-xs focus:border-primary 
                                 focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        {treeNames.map(name => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>

                      <div className="relative flex-1">
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={handleSearchChange}
                          placeholder="이름, 학교, 이메일, 과정 검색..."
                          className="w-full rounded-3xl border border-gray-300 px-3 py-1.5
                                   text-xs focus:border-primary focus:outline-none 
                                   focus:ring-1 focus:ring-primary"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 
                                     text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3">
                    <div className="space-y-2">
                      {filteredRankData?.map((stamp) => (
                        <div
                          key={stamp.stamp_id}
                          id={`stamp-${stamp.stamp_id}`}
                          onClick={() => handleCardClick(stamp)}
                          className={`cursor-pointer rounded-3xl border bg-white p-2.5 transition-shadow hover:shadow-md
                            ${selectedStamp?.stamp_id === stamp.stamp_id ? 'border-transparent ring-2 ring-primary' : 'border-gray-200'}`}
                        >
                          <div className="mb-1.5 flex items-center justify-between">
                            <div>
                              <h4 className="text-sm font-medium">
                                {isMasked ? maskName(stamp.student_name) : stamp.student_name}
                              </h4>
                              <p className="text-xs text-gray-600">
                                {isMasked ? maskEmail(stamp.student_email) : stamp.student_email}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                                #{stamp.rank}
                              </span>
                              <div className="text-right">
                                <div className="text-sm font-semibold">{stamp.rating}</div>
                                <RatingChange delta={stamp.delta} />
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center justify-between text-xs text-gray-600">
                            <div>
                              {isMasked ? maskSchoolName(stamp.school_name) : stamp.school_name} ({stamp.school_grade})
                            </div>
                            <div className="flex gap-1.5">
                              <span>{stamp.edu_ver}</span>
                              <span>·</span>
                              <span>{stamp.tree_name}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                      {filteredRankData?.length === 0 && (
                        <div className="py-4 text-center text-gray-500">
                          검색 결과가 없습니다
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {currentMobilePanel === 'exams' && selectedStamp && (
                <div className="flex h-full flex-col rounded-3xl bg-white shadow-lg">
                  <div className="border-b p-4">
                    <h3 className="mb-2 text-[14px] font-semibold">시험 목록</h3>
                    <div className="rounded-3xl bg-gray-100 p-3">
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs text-gray-600">폼레이팅:</span>
                        <span className="text-sm font-semibold">{selectedStamp.rating}</span>
                      </div>
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs text-gray-600">정답률:</span>
                        <span className="text-sm font-semibold">
                          {getAccuracyData(selectedStamp).accuracy}%
                          <span className="ml-1 text-xs font-normal text-gray-500">
                            ({getAccuracyData(selectedStamp).correctCount}/{getAccuracyData(selectedStamp).totalCount})
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-600">시험 횟수:</span>
                        <span className="text-sm font-semibold">{selectedStamp.exam_list?.length || 0}회</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto p-3">
                    <div className="space-y-2">
                      {selectedStamp.exam_list?.map((exam, index) => (
                        <div
                          key={exam.exam_id}
                          onClick={() => handleSelectExam(exam)}
                          className={`cursor-pointer rounded-3xl border bg-white p-2.5 transition-colors hover:bg-gray-50
                            ${activeExamId === exam.exam_id ? 'border-transparent ring-2 ring-primary' : 'border-gray-200'}`}
                        >
                          <div className="mb-1.5 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                                {selectedStamp.exam_list.length - index}회차
                              </span>
                              <span className="text-xs text-gray-500">
                                {formatDate(exam.updated_at)}
                              </span>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold">{exam.rating}</div>
                              <RatingChange delta={exam.delta} />
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between text-xs">
                            <div className="text-gray-600">
                              난이도: {(exam.difficulty / 100).toFixed(2)}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-green-600">{exam.correct_count}</span>
                              <span>/</span>
                              <span className="text-red-600">{exam.wrong_count}</span>
                              <span className="text-gray-500">
                                ({((exam.correct_count / (exam.correct_count + exam.wrong_count)) * 100).toFixed(0)}%)
                              </span>
                            </div>
                          </div>

                          <div className="mt-1.5 flex justify-between">
                            <span className="text-xs text-gray-500">TID: {exam.exam_id}</span>
                            <div className="flex gap-1">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenReportModal(exam);
                                }}
                                className="px-2 py-0.5 text-xs"
                              >
                                리포트
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/student/${selectedStamp.student_id}/stamp/${selectedStamp.stamp_id}/result/${exam.exam_id}`);
                                }}
                                className="bg-blue-500 px-2 py-0.5 text-xs hover:bg-blue-600"
                              >
                                POM
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {(!selectedStamp.exam_list || selectedStamp.exam_list.length === 0) && (
                        <div className="py-4 text-center text-gray-500">
                          시험 정보가 없습니다
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {currentMobilePanel === 'details' && activeExam && (
                <div className="flex flex-col gap-4 overflow-y-auto">
                  {/* 폼레이팅 변화 추이 (위치 변경) */}
                  <div className="rounded-3xl bg-white p-4 shadow-lg">
                    <h3 className="mb-3 text-[14px] font-bold text-gray-800">폼레이팅 변화 추이</h3>
                    
                    {/* 분리된 범례들 (모바일 버전) */}
                    <div className="mb-3 flex items-start justify-between">
                      {/* 좌측 상단: 폼레이팅 범례 */}
                      <div className="flex items-center gap-1">
                        <div className="h-2 w-3 rounded-3xl bg-blue-500"></div>
                        <span className="text-[10px] font-medium text-gray-600">폼레이팅</span>
                      </div>
                      
                      {/* 우측 상단: 난이도별 범례 */}
                      <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((level, index) => (
                          <div key={level} className="flex items-center gap-1">
                            <div 
                              className="h-2 w-3 rounded-3xl" 
                              style={{ backgroundColor: DIFFICULTY_COLORS[index] }}
                            ></div>
                            <span className="text-[8px] text-gray-600">Lv{level}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="h-44">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={getExamChartData(selectedStamp)}
                          margin={{ top: 5, right: 15, left: 15, bottom: 5 }}
                        >
                          <CartesianGrid 
                            strokeDasharray="3 3" 
                            stroke="#f0f0f0" 
                          />
                          <XAxis
                            dataKey="x"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 10, fill: "#6B7280" }}
                            dy={0}
                            tickMargin={3}
                          />
                          <YAxis 
                            yAxisId="left"
                            orientation="left"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: '#3B82F6', fontWeight: 'bold' }}
                            width={35}
                          />
                          <YAxis 
                            yAxisId="right"
                            orientation="right"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fontSize: 9, fill: "#22C55E", fontWeight: 'bold' }}
                            width={35}
                          />
                          <Tooltip
                            contentStyle={{ 
                              fontSize: '11px', 
                              padding: '6px 8px', 
                              borderRadius: '6px', 
                              border: '1px solid #ccc',
                              backgroundColor: 'white',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                            itemStyle={{ color: HEX_COLORS.headerBg }}
                            separator=""
                            labelFormatter={(label, payload) => {
                              const data = payload && payload[0] ? payload[0].payload : null;
                              const accuracy = data ? data.accuracy : 0;
                              return (
                                <div>
                                  <div>{label}회차</div>
                                  <div style={{ color: HEX_COLORS.primary, fontWeight: 'bold', marginTop: '2px' }}>
                                    정답률: {accuracy}%
                                  </div>
                                </div>
                              );
                            }}
                            formatter={(value, name, props) => {
                              if (name === 'rating') {
                                const { delta } = props.payload;
                                const deltaColor = delta > 0 ? '#ef4444' : delta < 0 ? '#3b82f6' : '#6b7280';
                                const deltaSymbol = delta > 0 ? '▲' : delta < 0 ? '▼' : '';
                                const deltaValue = delta !== 0 ? Math.abs(delta) : '';
                                
                                return [
                                  <div>
                                    <div style={{ color: '#3B82F6', fontWeight: 'bold' }}>
                                      폼레이팅: {value.toLocaleString()}
                                    </div>
                                    <div style={{ marginLeft: '12px', color: HEX_COLORS.headerBg, marginTop: '2px' }}>
                                      전 회차 대비 증감: {delta !== 0 ? (
                                        <span style={{ color: deltaColor, fontSize: '10px' }}>
                                          {deltaSymbol}{deltaValue}
                                        </span>
                                      ) : (
                                        <span style={{ color: '#6b7280', fontSize: '10px' }}>-</span>
                                      )}
                                    </div>
                                  </div>,
                                  ''
                                ];
                              } else if (name.startsWith('Level')) {
                                const { correctByLevel, totalCorrect } = props.payload;
                                const levelIndex = parseInt(name.split(' ')[1]) - 1;
                                const count = correctByLevel[levelIndex];
                                const percentage = totalCorrect > 0 ? ((count / totalCorrect) * 100).toFixed(1) : 0;
                                const levelColor = DIFFICULTY_COLORS[levelIndex];
                                
                                return [
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '12px' }}>
                                    <span 
                                      style={{ 
                                        display: 'inline-block',
                                        width: '8px', 
                                        height: '8px', 
                                        backgroundColor: levelColor,
                                        borderRadius: '1px'
                                      }}
                                    ></span>
                                    <span style={{ color: HEX_COLORS.headerBg }}>Lv{levelIndex + 1} {count}개 ({percentage}%)</span>
                                  </div>,
                                  ''
                                ];
                              }
                              return [`${value}%`, name];
                            }}
                          />
                          <Bar 
                            yAxisId="right"
                            dataKey="level1" 
                            stackId="difficulty"
                            fill={DIFFICULTY_COLORS[0]}
                            stroke={DIFFICULTY_COLORS[0]}
                            strokeWidth={0.5}
                            name="Level 1"
                            legendType="none"
                          />
                          <Bar 
                            yAxisId="right"
                            dataKey="level2" 
                            stackId="difficulty"
                            fill={DIFFICULTY_COLORS[1]}
                            stroke={DIFFICULTY_COLORS[1]}
                            strokeWidth={0.5}
                            name="Level 2"
                            legendType="none"
                          />
                          <Bar 
                            yAxisId="right"
                            dataKey="level3" 
                            stackId="difficulty"
                            fill={DIFFICULTY_COLORS[2]}
                            stroke={DIFFICULTY_COLORS[2]}
                            strokeWidth={0.5}
                            name="Level 3"
                            legendType="none"
                          />
                          <Bar 
                            yAxisId="right"
                            dataKey="level4" 
                            stackId="difficulty"
                            fill={DIFFICULTY_COLORS[3]}
                            stroke={DIFFICULTY_COLORS[3]}
                            strokeWidth={0.5}
                            name="Level 4"
                            legendType="none"
                          />
                          <Bar 
                            yAxisId="right"
                            dataKey="level5" 
                            stackId="difficulty"
                            fill={DIFFICULTY_COLORS[4]}
                            stroke={DIFFICULTY_COLORS[4]}
                            strokeWidth={0.5}
                            name="Level 5"
                            legendType="none"
                          />
                          <Line
                            yAxisId="left"
                            type="monotone"
                            dataKey="rating"
                            stroke="#3B82F6"
                            strokeWidth={2}
                            dot={{ r: 3, fill: '#3B82F6', strokeWidth: 2, stroke: '#FFF' }}
                            activeDot={{ r: 5, fill: '#3B82F6', stroke: '#FFF', strokeWidth: 2 }}
                            name="rating"
                            legendType="none"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  
                  {/* 시험 정보 상단 카드 (위치 변경 및 높이 조정) */}
                  <div className="rounded-3xl bg-white p-4 shadow-lg">
                    <h3 className="mb-3 text-[14px] font-bold text-gray-800">시험 정보</h3>
                    <div className="flex h-[176px] flex-col justify-center"> {/* 높이를 h-44(176px)로 조정 */}
                      <div className="mb-3 flex items-center justify-between">
                        <div>
                          <div className="text-sm text-gray-600">
                            {formatDate(activeExam.updated_at)} · {selectedStamp.tree_name} · {activeExam.length}문항
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold">Rating: {activeExam.rating}</div>
                          <div>
                            변화량: <RatingChange delta={activeExam.delta} />
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3">
                        <div className="flex flex-col items-center rounded-3xl bg-gray-50 p-3">
                          <span className="mb-1 text-[14px] font-semibold text-gray-700">정답률</span>
                          <p className="mb-0 text-xl font-bold text-gray-800">
                            {((activeExam.correct_count / (activeExam.correct_count + activeExam.wrong_count)) * 100).toFixed(0)}<span className="text-sm">%</span>
                          </p>
                          <div className="flex items-center text-xs">
                            <span className="text-green-600">{activeExam.correct_count}</span>
                            <span className="mx-1">/</span>
                            <span className="text-red-600">{activeExam.wrong_count}</span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-center rounded-3xl bg-gray-50 p-3">
                          <span className="mb-1 text-[14px] font-semibold text-gray-700">난이도</span>
                          <p className="mb-0 text-xl font-bold text-gray-800">
                            {(activeExam.difficulty / 100).toFixed(2)}
                          </p>
                          <div className="flex items-center text-xs">
                            <span className="text-gray-500">
                              {(activeExam.difficulty / 100) < 1.5 ? '하' : 
                               (activeExam.difficulty / 100) < 2.5 ? '중하' :
                               (activeExam.difficulty / 100) < 3.5 ? '중' :
                               (activeExam.difficulty / 100) < 4.5 ? '중상' : '상'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-center rounded-3xl bg-gray-50 p-3">
                          <span className="mb-1 text-[14px] font-semibold text-gray-700">TID</span>
                          <p className="mb-0 mt-1 text-sm font-medium text-gray-800">
                            {activeExam.exam_id}
                          </p>
                          <div className="flex items-center text-xs text-gray-500">
                            &nbsp;
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* 2열 그리드 레이아웃 */}
                  <div className="grid grid-cols-1 gap-4">
                    {/* 난이도별 정답률 */}
                    <div className="rounded-3xl bg-white p-4 shadow-lg">
                      <h3 className="mb-3 text-[14px] font-bold text-gray-800">난이도별 정답률</h3>
                      <div className="flex gap-2">
                        <div className="flex-1 space-y-3 rounded-3xl bg-gray-50 p-3">
                          {getLevelAchievementData(selectedStamp).map(item => (
                            <div key={item.level} className="grid grid-cols-[auto_1fr] items-center gap-x-2 text-[12px]">
                              <span className="w-8 font-medium text-gray-700">LV {item.level}</span>
                              <div className="space-y-1">
                                <div className="flex items-center">
                                  <div className="mr-2 h-[8px] grow overflow-hidden rounded-full bg-gray-100">
                                    <div
                                      className="h-full rounded-full bg-primary"
                                      style={{ width: `${item.currentAcc}%` }}
                                    ></div>
                                  </div>
                                  <span className="w-8 text-right text-gray-700">{item.currentAcc}%</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="space-y-[14px] rounded-3xl bg-gray-50 p-3"> 
                          {getLevelAchievementData(selectedStamp).map(item => (
                            <div key={item.level} className="text-right text-[12px]">
                              <div className="block font-semibold">
                                <span className="text-primary">{item.currentCorrect}</span>
                                <span className="text-gray-700">/{item.currentTotal}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* 난이도별 성취도 차트 */}
                    <div className="rounded-3xl bg-white p-4 shadow-lg">
                      <h3 className="mb-3 text-[14px] font-bold text-gray-800">난이도별 성취도</h3>
                      <div className="h-44">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={getLevelRadarData(selectedStamp)}>
                            <PolarGrid stroke="#E0E0E0" />
                            <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#7B7979' }} />
                            <PolarRadiusAxis angle={30} domain={[0, 100]} axisLine={false} tick={false} />
                            <Radar name="정답률" dataKey="A" stroke="#4ABC85" fill="#4ABC85" fillOpacity={0.6} />
                            <Tooltip
                              contentStyle={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', border: '1px solid #ccc' }}
                              separator=""
                              formatter={(value, name, props) => [`${props.payload.subject} ${value}%`, null]}
                            />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                  
                  {/* 문제 리스트 */}
                  <div className="rounded-3xl bg-white p-4 shadow-lg">
                    <h3 className="mb-3 text-[14px] font-bold text-gray-800">문제 리스트</h3>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">번호</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">결과</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">난이도</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">변화</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">보기</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {activeExam.sequence.map((problem, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                              <td className="px-3 py-2 text-sm">{index + 1}</td>
                              <td className="px-3 py-2 text-sm">
                                {problem[6] ? (
                                  <span className="font-bold text-blue-500">O</span>
                                ) : (
                                  <span className="font-bold text-red-500">X</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm">{problem[1]}</td>
                              <td className="px-3 py-2 text-sm">
                                <RatingChange delta={problem[5]} />
                              </td>
                              <td className="px-3 py-2 text-sm">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleOpenProblemPreview(problem, index)}
                                  className="p-1 text-gray-600"
                                  title="문제 보기"
                                >
                                  <Eye size={16} />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // 데스크톱 뷰 (기존 코드 유지)
            <div className="flex h-[calc(100vh-7rem)] gap-4">
              {/* 1번 영역 (좌측 학생 목록) */}
              <div className="flex w-[24%] flex-col overflow-hidden rounded-3xl bg-white shadow-lg">
                <div className="sticky top-0 z-10 space-y-3 border-b bg-white p-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-[14px] font-semibold">학생 목록</h3>
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-1 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={isMasked}
                          onChange={handleMaskingChange}
                          className="rounded text-primary focus:ring-primary"
                        />
                        마스킹
                      </label>
                      <span className="text-xs text-gray-500">
                        {filteredRankData?.length || 0} / {rankData?.length || 0}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={selectedTree}
                      onChange={(e) => setSelectedTree(e.target.value)}
                      className="w-36 rounded-3xl border border-gray-300 px-3
                               py-1.5 text-xs focus:border-primary 
                               focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      {treeNames.map(name => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>

                    <div className="relative flex-1">
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        placeholder="이름, 학교, 이메일, 과정 검색..."
                        className="w-full rounded-3xl border border-gray-300 px-3 py-1.5
                                 text-xs focus:border-primary focus:outline-none 
                                 focus:ring-1 focus:ring-primary"
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 
                                   text-gray-400 hover:text-gray-600"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3">
                  <div className="space-y-2">
                    {filteredRankData?.map((stamp) => (
                      <div
                        key={stamp.stamp_id}
                        id={`stamp-${stamp.stamp_id}`}
                        onClick={() => handleCardClick(stamp)}
                        className={`cursor-pointer rounded-3xl border bg-white p-2.5 transition-shadow hover:shadow-md
                          ${selectedStamp?.stamp_id === stamp.stamp_id ? 'border-transparent ring-2 ring-primary' : 'border-gray-200'}`}
                      >
                        <div className="mb-1.5 flex items-center justify-between">
                          <div>
                            <h4 className="text-sm font-medium">
                              {isMasked ? maskName(stamp.student_name) : stamp.student_name}
                            </h4>
                            <p className="text-xs text-gray-600">
                              {isMasked ? maskEmail(stamp.student_email) : stamp.student_email}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                              #{stamp.rank}
                            </span>
                            <div className="text-right">
                              <div className="text-sm font-semibold">{stamp.rating}</div>
                              <RatingChange delta={stamp.delta} />
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <div>
                            {isMasked ? maskSchoolName(stamp.school_name) : stamp.school_name} ({stamp.school_grade})
                          </div>
                          <div className="flex gap-1.5">
                            <span>{stamp.edu_ver}</span>
                            <span>·</span>
                            <span>{stamp.tree_name}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredRankData?.length === 0 && (
                      <div className="py-4 text-center text-gray-500">
                        검색 결과가 없습니다
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 2번 영역 (중간 시험 목록) */}
              <div className="flex w-[23%] flex-col gap-4">
                {selectedStamp ? (
                  <div className="flex h-full flex-col rounded-3xl bg-white shadow-lg">
                    <div className="border-b p-4">
                      <h3 className="mb-2 text-[14px] font-semibold">시험 목록</h3>
                      <div className="rounded-3xl bg-gray-100 p-3">
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-xs text-gray-600">폼레이팅:</span>
                          <span className="text-sm font-semibold">{selectedStamp.rating}</span>
                        </div>
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-xs text-gray-600">정답률:</span>
                          <span className="text-sm font-semibold">
                            {getAccuracyData(selectedStamp).accuracy}%
                            <span className="ml-1 text-xs font-normal text-gray-500">
                              ({getAccuracyData(selectedStamp).correctCount}/{getAccuracyData(selectedStamp).totalCount})
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-600">시험 횟수:</span>
                          <span className="text-sm font-semibold">{selectedStamp.exam_list?.length || 0}회</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-3">
                      <div className="space-y-2">
                        {selectedStamp.exam_list?.map((exam, index) => (
                          <div
                            key={exam.exam_id}
                            onClick={() => handleSelectExam(exam)}
                            className={`cursor-pointer rounded-3xl border bg-white p-2.5 transition-colors hover:bg-gray-50
                              ${activeExamId === exam.exam_id ? 'border-transparent ring-2 ring-primary' : 'border-gray-200'}`}
                          >
                            <div className="mb-1.5 flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                                  {selectedStamp.exam_list.length - index}회차
                                </span>
                                <span className="text-xs text-gray-500">
                                  {formatDate(exam.updated_at)}
                                </span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm font-semibold">{exam.rating}</div>
                                <RatingChange delta={exam.delta} />
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between text-xs">
                              <div className="text-gray-600">
                                난이도: {(exam.difficulty / 100).toFixed(2)}
                              </div>
                              <div className="flex items-center gap-1">
                                <span className="text-green-600">{exam.correct_count}</span>
                                <span>/</span>
                                <span className="text-red-600">{exam.wrong_count}</span>
                                <span className="text-gray-500">
                                  ({((exam.correct_count / (exam.correct_count + exam.wrong_count)) * 100).toFixed(0)}%)
                                </span>
                              </div>
                            </div>

                            <div className="mt-1.5 flex justify-between">
                              <span className="text-xs text-gray-500">TID: {exam.exam_id}</span>
                              <div className="flex gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenReportModal(exam);
                                  }}
                                  className="rounded bg-primary px-2 py-0.5 text-xs text-white transition-colors hover:bg-[#36916E]"
                                >
                                  리포트
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/student/${selectedStamp.student_id}/stamp/${selectedStamp.stamp_id}/result/${exam.exam_id}`);
                                  }}
                                  className="rounded bg-blue-500 px-2 py-0.5 text-xs text-white transition-colors hover:bg-blue-600"
                                >
                                  POM
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                        {(!selectedStamp.exam_list || selectedStamp.exam_list.length === 0) && (
                          <div className="py-4 text-center text-gray-500">
                            시험 정보가 없습니다
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center rounded-3xl bg-white shadow-lg">
                    <p className="text-gray-500">좌측에서 학생을 선택하세요</p>
                  </div>
                )}
              </div>

              {/* 3번 영역 (우측 리포트) */}
              <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
                {activeExam ? (
                  <>
                    {/* 폼레이팅 변화 추이 (위치 변경) */}
                    <div className="rounded-3xl bg-white p-4 shadow-lg">
                      <h3 className="mb-3 text-[14px] font-bold text-gray-800">폼레이팅 변화 추이</h3>
                      
                      {/* 분리된 범례들 */}
                      <div className="mb-3 flex items-start justify-between">
                        {/* 좌측 상단: 폼레이팅 범례 */}
                        <div className="flex items-center gap-1">
                          <div className="h-2 w-3 rounded-3xl bg-blue-500"></div>
                          <span className="text-[10px] font-medium text-gray-600">폼레이팅</span>
                        </div>
                        
                        {/* 우측 상단: 난이도별 범례 */}
                        <div className="flex items-center gap-3">
                          {[1, 2, 3, 4, 5].map((level, index) => (
                            <div key={level} className="flex items-center gap-1">
                              <div 
                                className="h-2 w-3 rounded-3xl" 
                                style={{ backgroundColor: DIFFICULTY_COLORS[index] }}
                              ></div>
                              <span className="text-[9px] text-gray-600">Lv{level}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="h-44">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart
                            data={getExamChartData(selectedStamp)}
                            margin={{ top: 5, right: 0, left: 0, bottom: 5 }}
                          >
                            <CartesianGrid 
                              strokeDasharray="3 3" 
                              stroke="#f0f0f0" 
                            />
                            <XAxis
                              dataKey="x"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 10, fill: "#6B7280" }}
                              dy={0}
                              tickMargin={3}
                            />
                            <YAxis 
                              yAxisId="left"
                              orientation="left"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 9, fill: '#3B82F6', fontWeight: 'bold' }}
                              width={35}
                            />
                            <YAxis 
                              yAxisId="right"
                              orientation="right"
                              axisLine={false}
                              tickLine={false}
                              tick={{ fontSize: 9, fill: "#22C55E", fontWeight: 'bold' }}
                              width={35}
                            />
                            <Tooltip
                              contentStyle={{ 
                                fontSize: '11px', 
                                padding: '6px 8px', 
                                borderRadius: '6px', 
                                border: '1px solid #ccc',
                                backgroundColor: 'white',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                              }}
                              itemStyle={{ color: HEX_COLORS.headerBg }}
                              separator=""
                              labelFormatter={(label, payload) => {
                                const data = payload && payload[0] ? payload[0].payload : null;
                                const accuracy = data ? data.accuracy : 0;
                                return (
                                  <div>
                                    <div>{label}회차</div>
                                    <div style={{ color: HEX_COLORS.primary, fontWeight: 'bold', marginTop: '2px' }}>
                                      정답률: {accuracy}%
                                    </div>
                                  </div>
                                );
                              }}
                              formatter={(value, name, props) => {
                                if (name === 'rating') {
                                  const { delta } = props.payload;
                                  const deltaColor = delta > 0 ? '#ef4444' : delta < 0 ? '#3b82f6' : '#6b7280';
                                  const deltaSymbol = delta > 0 ? '▲' : delta < 0 ? '▼' : '';
                                  const deltaValue = delta !== 0 ? Math.abs(delta) : '';
                                  
                                  return [
                                    <div>
                                      <div style={{ color: '#3B82F6', fontWeight: 'bold' }}>
                                        폼레이팅: {value.toLocaleString()}
                                      </div>
                                      <div style={{ marginLeft: '12px', color: HEX_COLORS.headerBg, marginTop: '2px' }}>
                                        전 회차 대비 증감: {delta !== 0 ? (
                                          <span style={{ color: deltaColor, fontSize: '10px' }}>
                                            {deltaSymbol}{deltaValue}
                                          </span>
                                        ) : (
                                          <span style={{ color: '#6b7280', fontSize: '10px' }}>-</span>
                                        )}
                                      </div>
                                    </div>,
                                    ''
                                  ];
                                } else if (name.startsWith('Level')) {
                                  const { correctByLevel, totalCorrect } = props.payload;
                                  const levelIndex = parseInt(name.split(' ')[1]) - 1;
                                  const count = correctByLevel[levelIndex];
                                  const percentage = totalCorrect > 0 ? ((count / totalCorrect) * 100).toFixed(1) : 0;
                                  const levelColor = DIFFICULTY_COLORS[levelIndex];
                                  
                                  return [
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: '12px' }}>
                                      <span 
                                        style={{ 
                                          display: 'inline-block',
                                          width: '8px', 
                                          height: '8px', 
                                          backgroundColor: levelColor,
                                          borderRadius: '1px'
                                        }}
                                      ></span>
                                      <span style={{ color: HEX_COLORS.headerBg }}>Lv{levelIndex + 1} {count}개 ({percentage}%)</span>
                                    </div>,
                                    ''
                                  ];
                                }
                                return [`${value}%`, name];
                              }}
                            />
                            <Bar 
                              yAxisId="right"
                              dataKey="level1" 
                              stackId="difficulty"
                              fill={DIFFICULTY_COLORS[0]}
                              stroke={DIFFICULTY_COLORS[0]}
                              strokeWidth={0.5}
                              name="Level 1"
                              legendType="none"
                            />
                            <Bar 
                              yAxisId="right"
                              dataKey="level2" 
                              stackId="difficulty"
                              fill={DIFFICULTY_COLORS[1]}
                              stroke={DIFFICULTY_COLORS[1]}
                              strokeWidth={0.5}
                              name="Level 2"
                              legendType="none"
                            />
                            <Bar 
                              yAxisId="right"
                              dataKey="level3" 
                              stackId="difficulty"
                              fill={DIFFICULTY_COLORS[2]}
                              stroke={DIFFICULTY_COLORS[2]}
                              strokeWidth={0.5}
                              name="Level 3"
                              legendType="none"
                            />
                            <Bar 
                              yAxisId="right"
                              dataKey="level4" 
                              stackId="difficulty"
                              fill={DIFFICULTY_COLORS[3]}
                              stroke={DIFFICULTY_COLORS[3]}
                              strokeWidth={0.5}
                              name="Level 4"
                              legendType="none"
                            />
                            <Bar 
                              yAxisId="right"
                              dataKey="level5" 
                              stackId="difficulty"
                              fill={DIFFICULTY_COLORS[4]}
                              stroke={DIFFICULTY_COLORS[4]}
                              strokeWidth={0.5}
                              name="Level 5"
                              legendType="none"
                            />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="rating"
                              stroke="#3B82F6"
                              strokeWidth={2}
                              dot={{ r: 3, fill: '#3B82F6', strokeWidth: 2, stroke: '#FFF' }}
                              activeDot={{ r: 5, fill: '#3B82F6', stroke: '#FFF', strokeWidth: 2 }}
                              name="rating"
                              legendType="none"
                            />
                          </ComposedChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                    
                    {/* 시험 정보 상단 카드 (위치 변경 및 높이 조정) */}
                    <div className="rounded-3xl bg-white p-4 shadow-lg">
                      <h3 className="mb-3 text-[14px] font-bold text-gray-800">시험 정보</h3>
                      <div className="flex h-[176px] flex-col justify-center"> {/* 높이를 h-44(176px)로 조정 */}
                        <div className="mb-3 flex items-center justify-between">
                          <div>
                            <div className="text-sm text-gray-600">
                              {formatDate(activeExam.updated_at)} · {selectedStamp.tree_name} · {activeExam.length}문항
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-bold">Rating: {activeExam.rating}</div>
                            <div>
                              변화량: <RatingChange delta={activeExam.delta} />
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3">
                          <div className="flex flex-col items-center rounded-3xl bg-gray-50 p-3">
                            <span className="mb-1 text-[14px] font-semibold text-gray-700">정답률</span>
                            <p className="mb-0 text-xl font-bold text-gray-800">
                              {((activeExam.correct_count / (activeExam.correct_count + activeExam.wrong_count)) * 100).toFixed(0)}<span className="text-sm">%</span>
                            </p>
                            <div className="flex items-center text-xs">
                              <span className="text-green-600">{activeExam.correct_count}</span>
                              <span className="mx-1">/</span>
                              <span className="text-red-600">{activeExam.wrong_count}</span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-center rounded-3xl bg-gray-50 p-3">
                            <span className="mb-1 text-[14px] font-semibold text-gray-700">난이도</span>
                            <p className="mb-0 text-xl font-bold text-gray-800">
                              {(activeExam.difficulty / 100).toFixed(2)}
                            </p>
                            <div className="flex items-center text-xs">
                              <span className="text-gray-500">
                                {(activeExam.difficulty / 100) < 1.5 ? '하' : 
                                 (activeExam.difficulty / 100) < 2.5 ? '중하' :
                                 (activeExam.difficulty / 100) < 3.5 ? '중' :
                                 (activeExam.difficulty / 100) < 4.5 ? '중상' : '상'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex flex-col items-center rounded-3xl bg-gray-50 p-3">
                            <span className="mb-1 text-[14px] font-semibold text-gray-700">TID</span>
                            <p className="mb-0 mt-1 text-sm font-medium text-gray-800">
                              {activeExam.exam_id}
                            </p>
                            <div className="flex items-center text-xs text-gray-500">
                              &nbsp;
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* 2열 그리드 레이아웃 */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* 난이도별 정답률 */}
                      <div className="rounded-3xl bg-white p-4 shadow-lg">
                        <h3 className="mb-3 text-[14px] font-bold text-gray-800">난이도별 정답률</h3>
                        <div className="flex gap-2">
                          <div className="flex-1 space-y-3 rounded-3xl bg-gray-50 p-3">
                            {getLevelAchievementData(selectedStamp).map(item => (
                              <div key={item.level} className="grid grid-cols-[auto_1fr] items-center gap-x-2 text-[12px]">
                                <span className="w-8 font-medium text-gray-700">LV {item.level}</span>
                                <div className="space-y-1">
                                  <div className="flex items-center">
                                    <div className="mr-2 h-[8px] grow overflow-hidden rounded-full bg-gray-100">
                                      <div
                                        className="h-full rounded-full bg-primary"
                                        style={{ width: `${item.currentAcc}%` }}
                                      ></div>
                                    </div>
                                    <span className="w-8 text-right text-gray-700">{item.currentAcc}%</span>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="space-y-[14px] rounded-3xl bg-gray-50 p-3"> 
                            {getLevelAchievementData(selectedStamp).map(item => (
                              <div key={item.level} className="text-right text-[12px]">
                                <div className="block font-semibold">
                                  <span className="text-primary">{item.currentCorrect}</span>
                                  <span className="text-gray-700">/{item.currentTotal}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* 난이도별 정답률 레이더 차트 */}
                      <div className="rounded-3xl bg-white p-4 shadow-lg">
                        <h3 className="mb-3 text-[14px] font-bold text-gray-800">난이도별 성취도</h3>
                        <div className="h-44">
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={getLevelRadarData(selectedStamp)}>
                              <PolarGrid stroke="#E0E0E0" />
                              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: '#7B7979' }} />
                              <PolarRadiusAxis angle={30} domain={[0, 100]} axisLine={false} tick={false} />
                              <Radar name="정답률" dataKey="A" stroke="#4ABC85" fill="#4ABC85" fillOpacity={0.6} />
                              <Tooltip
                                contentStyle={{ fontSize: '10px', padding: '2px 5px', borderRadius: '4px', border: '1px solid #ccc' }}
                                separator=""
                                formatter={(value, name, props) => [`${props.payload.subject} ${value}%`, null]}
                              />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                    
                    {/* 문제 리스트 */}
                    <div className="rounded-3xl bg-white p-4 shadow-lg">
                      <h3 className="mb-3 text-[14px] font-bold text-gray-800">문제 리스트</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">번호</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">문제 ID</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">결과</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">난이도</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">문제 Rating</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">기대 정답률</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Rating</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">변화량</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">보기</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {activeExam.sequence.map((problem, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                                <td className="px-3 py-2 text-sm">{index + 1}</td>
                                <td className="px-3 py-2 text-sm">{problem[0]}</td>
                                <td className="px-3 py-2 text-sm">
                                  {problem[6] ? (
                                    <span className="font-bold text-blue-500">O</span>
                                  ) : (
                                    <span className="font-bold text-red-500">X</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 text-sm">{problem[1]}</td>
                                <td className="px-3 py-2 text-sm">{problem[2]}</td>
                                <td className="px-3 py-2 text-sm">{problem[3]}%</td>
                                <td className="px-3 py-2 text-sm">{problem[4]}</td>
                                <td className="px-3 py-2 text-sm">
                                  <RatingChange delta={problem[5]} />
                                </td>
                                <td className="px-3 py-2 text-sm">
                                  <button 
                                    onClick={() => handleOpenProblemPreview(problem, index)}
                                    className="rounded p-1 text-gray-600 hover:bg-gray-100"
                                    title="문제 보기"
                                  >
                                    <Eye size={16} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : selectedStamp ? (
                  <div className="flex h-full items-center justify-center rounded-3xl bg-white shadow-lg">
                    <p className="text-gray-500">중간 패널에서 시험을 선택하세요</p>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center rounded-3xl bg-white shadow-lg">
                    <p className="text-gray-500">학생을 선택하세요</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      {/* 모달 */}
      {selectedExam && (
        <ExamDetailModal
          exam={selectedExam}
          round={selectedRound}
          onClose={() => {
            setSelectedExam(null);
            setSelectedRound(null);
          }}
        />
      )}

      {/* 리포트 모달 */}
      <ReportModal 
        isOpen={isReportModalOpen} 
        onClose={() => setIsReportModalOpen(false)}
        examData={reportExamData}
      />

      {/* 문제 보기 모달 */}
      <ProblemPreview 
        isOpen={isProblemPreviewOpen}
        onClose={() => setIsProblemPreviewOpen(false)}
        problemIds={previewProblemId}
        showNumbers={true}
        correctList={previewCorrect}
        problemNumbers={previewNumber}
      />
    </Layout>
  );
}

export default Datapage;